import { useEffect, useRef, useState } from 'react';
import { newAttendanceOperationKey } from '../../hooks/useAttendanceCapture.ts';
import { AttendanceContext, AttendanceTarget } from './attendanceCapture.ts';

// Headless version of AttendanceCreateCaptureFields' context load: the Create
// Lead step now draws the consultation date and dentist from its ordinary
// Team + Appointment form, so only the backend context (dentists, server time,
// capabilities) and the operation key are needed here.
export type AttendanceCreateContextState = {
    context: AttendanceContext | null;
    error: string;
    loadedAt: number;
    operationKey: string;
};

export default function useAttendanceCreateContext(practiceId: number | null, targetStatus: Exclude<AttendanceTarget, null> | null, draftOperationKey?: string): AttendanceCreateContextState {
    const [context, setContext] = useState<AttendanceContext | null>(null);
    const [error, setError] = useState('');
    const loadedAt = useRef(0);
    const operationKey = useRef(draftOperationKey || newAttendanceOperationKey());

    useEffect(() => {
        let current = true;
        setContext(null); setError('');
        if (!practiceId || !targetStatus) return () => { current = false; };
        import('../../utils/apiCall').then(({ callApi }) => callApi(`/api/v1/practices/${practiceId}/leads/attendance-create-context/?target_status=${targetStatus}`, 'GET'))
            .then((response: any) => {
                if (!current) return;
                const data = response?.data;
                if (response?.status !== 200 || String(data?.practice_id) !== String(practiceId) || data?.target_status !== targetStatus || data?.lead_id != null || data?.treatment_id != null) {
                    setError('Attendance options changed practice or are unavailable. Reopen this step.'); return;
                }
                loadedAt.current = performance.now(); setContext(data);
            }).catch(() => current && setError('Attendance options are unavailable. Reopen this step.'));
        return () => { current = false; };
    }, [practiceId, targetStatus]);

    return { context, error, loadedAt: loadedAt.current, operationKey: operationKey.current };
}
